import { create } from 'zustand';

/** The org resolved from the current /:orgSlug/* mount (PLAN_MULTI_ORG_ADMIN.md). */
export interface ActiveOrg {
  id: number;
  slug: string;
  name: string;
  logo_url?: string | null;
  logo_black_url?: string | null;
  logo_white_url?: string | null;
}

/** An org the current user may access (from GET /organizations/my-organizations). */
export interface OrgOption {
  id: number;
  name: string;
  slug: string | null;
  logo_url?: string | null;
}

interface OrgContextState {
  activeOrg: ActiveOrg | null;
  setActiveOrg: (org: ActiveOrg | null) => void;
  /**
   * Per-org tab override used in the /all (multi-org) mode: pages rendered inside
   * an <OrgTabs> get their API calls scoped to this org via X-Organization-Id.
   */
  forcedOrgId: number | null;
  setForcedOrgId: (id: number | null) => void;
  /** Orgs available to the user — switcher options, filter options, id→slug lookup. */
  myOrgs: OrgOption[];
  setMyOrgs: (orgs: OrgOption[]) => void;
}

export const useOrgContextStore = create<OrgContextState>((set) => ({
  activeOrg: null,
  setActiveOrg: (activeOrg) => set({ activeOrg }),
  forcedOrgId: null,
  setForcedOrgId: (forcedOrgId) => set({ forcedOrgId }),
  myOrgs: [],
  setMyOrgs: (myOrgs) => set({ myOrgs }),
}));

/**
 * The organization id every API request should be scoped to right now, or null.
 * - /:orgSlug/* mount → the resolved org.
 * - /all/* mount → the org of the currently selected tab (billing/agencies/clients), if any.
 */
export function currentOrgHeader(): string | null {
  const { activeOrg, forcedOrgId } = useOrgContextStore.getState();
  const seg = window.location.pathname.split('/')[1] || '';
  if (seg === 'all') return forcedOrgId ? String(forcedOrgId) : null;
  if (activeOrg && seg === activeOrg.slug) return String(activeOrg.id);
  return null;
}
